if (!customElements.get('search-overlay')) {
  customElements.define('search-overlay', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.empty = this.querySelector('[data-search-empty]');
      this.status = this.querySelector('[data-search-status]');
      this.categories = this.querySelector('[data-search-categories]');
      this.viewAll = this.querySelector('[data-search-view-all]');
      this.recent = this.querySelector('[data-search-recent]');
      this.historyList = this.querySelector('[data-search-history]');
      this.historyTemplate = this.querySelector('[data-search-history-template]');
      this.historyKey = 'boc:recent-searches:v1';
      this.history = [];
      this.historyStorageUnavailable = false;
      this.readHistory();
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-search-overlay-trigger]');
        if (!trigger || trigger.getAttribute('aria-controls') !== this.dialog.id || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.trigger = trigger;
        this.readHistory();
        this.dialog.showModal();
        document.body.classList.add('search-overlay-open');
        this.input.focus({ preventScroll: true });
        if (this.query && this.loadedQuery !== this.query) this.search();
      }, options);

      this.querySelector('[data-search-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        this.rememberCompletedSearch();
        this.cancelRequest();
        document.body.classList.remove('search-overlay-open');
        this.trigger?.focus({ preventScroll: true });
      }, options);
      this.input.addEventListener('input', () => this.onInput(), options);
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.rememberSearch(this.query);
        const selected = this.results.querySelector('[aria-selected="true"] a');
        if (selected) selected.click();
        else if (this.query) this.search();
      }, options);
      this.querySelector('[data-search-history-clear]').addEventListener('click', () => {
        this.history = [];
        this.writeHistory();
        this.input.focus({ preventScroll: true });
      }, options);
      this.empty.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-search-history-remove]');
        if (remove) {
          const buttons = Array.from(this.historyList.querySelectorAll('[data-search-history-remove]'));
          const index = buttons.indexOf(remove);
          this.history = this.history.filter((query) => query !== remove.dataset.searchHistoryRemove);
          this.writeHistory();
          const remaining = this.historyList.querySelectorAll('[data-search-history-remove]');
          (remaining[Math.min(index, remaining.length - 1)] || this.input).focus({ preventScroll: true });
          return;
        }
        const suggestion = event.target.closest('[data-search-query]');
        if (!suggestion) return;
        this.input.value = suggestion.dataset.searchQuery;
        this.onInput();
        this.rememberSearch(this.query);
        this.input.focus({ preventScroll: true });
        this.search();
      }, options);
      this.results.addEventListener('click', (event) => {
        if (event.target.closest('[data-search-result]')) this.rememberSearch(this.query);
      }, options);
      this.viewAll.addEventListener('click', () => this.rememberSearch(this.query), options);
      window.addEventListener('storage', (event) => {
        if (event.key === this.historyKey || event.key === null) this.readHistory();
      }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.cancelRequest();
      document.body.classList.remove('search-overlay-open');
    }

    get query() {
      return this.input.value.trim();
    }

    readHistory() {
      let stored;
      if (this.historyStorageUnavailable) {
        this.renderHistory();
        return;
      }
      try {
        stored = localStorage.getItem(this.historyKey);
      } catch {
        // Search still works when browser storage is unavailable.
        this.historyStorageUnavailable = true;
        this.renderHistory();
        return;
      }
      try {
        stored = JSON.parse(stored || '[]');
      } catch {
        stored = [];
      }
      const seen = new Set();
      this.history = (Array.isArray(stored) ? stored : []).filter((query) => {
        if (typeof query !== 'string' || !query.trim() || query.length > 120) return false;
        const key = query.trim().toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, 5).map((query) => query.trim());
      this.renderHistory();
    }

    writeHistory() {
      if (!this.historyStorageUnavailable) {
        try {
          if (this.history.length) localStorage.setItem(this.historyKey, JSON.stringify(this.history));
          else localStorage.removeItem(this.historyKey);
        } catch {
          // Keep recent searches in memory for this page when storage is blocked.
          this.historyStorageUnavailable = true;
        }
      }
      this.renderHistory();
    }

    renderHistory() {
      this.historyList.replaceChildren();
      this.recent.hidden = this.history.length === 0;
      this.history.forEach((query) => {
        const row = this.historyTemplate.content.cloneNode(true);
        row.querySelector('[data-search-query]').dataset.searchQuery = query;
        row.querySelector('[data-search-history-label]').textContent = query;
        const remove = row.querySelector('[data-search-history-remove]');
        remove.dataset.searchHistoryRemove = query;
        remove.setAttribute('aria-label', remove.getAttribute('aria-label').replace('__query__', () => query));
        this.historyList.append(row);
      });
    }

    rememberSearch(query) {
      if (!query || query.length > 120) return;
      this.readHistory();
      this.history = [query, ...this.history.filter((item) => item.toLowerCase() !== query.toLowerCase())].slice(0, 5);
      this.writeHistory();
    }

    rememberCompletedSearch() {
      if (this.query === this.loadedQuery) this.rememberSearch(this.query);
    }

    cancelRequest() {
      clearTimeout(this.timer);
      this.request?.abort();
    }

    clearSelection() {
      this.results.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
      this.input.removeAttribute('aria-activedescendant');
    }

    onInput() {
      if (!this.query && this.loadedQuery) this.rememberSearch(this.loadedQuery);
      this.cancelRequest();
      this.clearSelection();
      this.loadedQuery = null;
      this.empty.hidden = Boolean(this.query);
      this.categories.hidden = Boolean(this.query);
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.viewAll.hidden = !this.query;
      this.status.hidden = !this.query;
      this.status.classList.remove('visually-hidden');
      this.status.textContent = this.query ? this.dataset.loadingText : '';
      this.results.setAttribute('aria-busy', Boolean(this.query));
      if (!this.query) return;
      const url = new URL(this.form.action);
      url.searchParams.set('q', this.query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      this.viewAll.href = url.href;
      this.timer = setTimeout(() => this.search(), 250);
    }

    async search() {
      this.cancelRequest();
      const query = this.query;
      if (!query) return;
      const request = new AbortController();
      this.request = request;
      this.status.hidden = false;
      this.status.classList.remove('visually-hidden');
      this.status.textContent = this.dataset.loadingText;
      this.results.setAttribute('aria-busy', 'true');
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('section_id', 'search-overlay-results');
      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        if (request.signal.aborted || this.query !== query || !this.dialog.open) return;
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-search-overlay-results');
        if (!section) throw new Error('Missing search results');
        this.clearSelection();
        this.results.innerHTML = section.innerHTML;
        this.results.hidden = false;
        this.results.setAttribute('aria-busy', 'false');
        this.loadedQuery = query;
        this.input.setAttribute('aria-expanded', 'true');
        this.status.textContent = section.querySelector('[data-search-result-count]')?.textContent || '';
        this.status.classList.add('visually-hidden');
      } catch (error) {
        if (request.signal.aborted || this.query !== query) return;
        this.results.hidden = true;
        this.results.setAttribute('aria-busy', 'false');
        this.input.setAttribute('aria-expanded', 'false');
        this.status.classList.remove('visually-hidden');
        this.status.textContent = this.dataset.errorText;
      }
    }

    onKeydown(event) {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.results.hidden) return;
      const options = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      event.preventDefault();
      const selectedIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
      const nextIndex = event.key === 'ArrowDown'
        ? (selectedIndex + 1) % options.length
        : (selectedIndex <= 0 ? options.length : selectedIndex) - 1;
      this.clearSelection();
      const selected = options[nextIndex];
      selected.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', selected.id);
      selected.scrollIntoView({ block: 'nearest' });
    }
  });
}
